import axios from '../../axios'
import Axios, { AxiosRequestConfig } from 'axios'
import { action, makeObservable, observable, runInAction } from 'mobx'
import { toast } from 'react-toastify'
import { FileStorage, CreateFileStorageData, UpdateFileStorageData, TemporaryFile } from './types'
import { Progress } from '@aws-sdk/lib-storage'
import FileUploader from 'app/services/FileUploader'
import {
  getFileNameFromSignedUrl,
  getFileNameFromUrl,
} from 'app/helpers/fileHelpers'
import { DEL_DELIVERY_TYPE } from 'app/constants'
import { Auth } from 'aws-amplify'
export class FileStoragesStore {
  loadingList: boolean = false
  list: FileStorage[] = []
  entries: {
    [key: string]: FileStorage
  } = {}
  totalPages: number = 0
  totalElements: number = 0
  cancelTokenSource = () => {}

  constructor() {
    makeObservable(this, {
      loadingList: observable,
      list: observable,
      entries: observable,
      totalPages: observable,
      totalElements: observable,
      getData: action,
      delete: action,
      setFileStorage: action,
      reset: action,
      clearList: action,
      removeFromList: action,
      setLoadingList: action,
    })
  }

  reset = () => {
    this.clearList()
    this.entries = {}
    this.cancelTokenSource = () => {}
  }

  clearList = () => {
    this.loadingList = false
    this.list = []
    this.totalPages = 0
    this.totalElements = 0
  }

  setLoadingList = (value: boolean) => {
    this.loadingList = value
  }

  getUploadUrl: (type: string) => Promise<string> = async (type) => {
    return await axios('/api/v1/dataset/file/upload/url?type=' + type).then(
      ({ data }) => data
    )
  }

  uploadFile = async (fileRef: any) => {
    const onProgress = (event: Progress) => {
      // @ts-ignore
      fileRef.progress = Math.round((100 * event.loaded) / event.total)
      // Re-render the file list to reflect the new progress
      fileRef.updateView()
    }

    try {
      fileRef.url = await FileUploader.uploadFile(
        fileRef.file,
        this.getUploadUrl,
        onProgress
      )
      fileRef.uploaded = true
      fileRef.uploading = false
      // Re-render the file list to reflect the new changes (uploaded = true)
      fileRef.updateView()
      return fileRef.url
    } catch (error) {
      toast.error('Failed to upload file. Please try again.')
      fileRef.progress = 0
      fileRef.error = true
      throw error
    }
  }

  removeFromList = (id: number) => {
    this.list = this.list.filter((item) => item.id !== id)
  }

  create = async (data: CreateFileStorageData) => {
    let requestElements = {}
    const newlyAddedDocuments: TemporaryFile[] = [];

    for (const file of data.files) {
      // @ts-ignore
      let signedUrl = file.url
      if (!signedUrl) {
        signedUrl = await this.uploadFile(file)
      }
      const originalFileName = file['file']['name']
      newlyAddedDocuments.push({originalFileName: originalFileName, temporaryFileName: getFileNameFromSignedUrl(signedUrl)});   
    }
      requestElements = {
        name: data.name,
        description: data.description,
        files: newlyAddedDocuments,
        delDeliveryType: data.delDeliveryType,
      }
    
    return await axios
      .post('/api/v1/file-storage', {
        ...requestElements,
      })
      .then((response) => {
        toast.success('Created successfully')
        return response
      })
  }

  update = async (id: number, data: UpdateFileStorageData) => {
    const newlyAddedFiles: TemporaryFile[] = [];
    const deletedFiles: string[] = []

    if (data.newlyAddedFiles) {
      for (const file of data.newlyAddedFiles) {
        let signedUrl: string = file['file']['url']
        if (!signedUrl) {
          signedUrl = await this.uploadFile(file)
        }
        const originalFileName = file['file']['name']
        newlyAddedFiles.push({originalFileName: originalFileName, temporaryFileName: getFileNameFromSignedUrl(signedUrl)});           
      }
    }

    if (data.deletedFiles) {
      for (const file of data.deletedFiles) {
          deletedFiles.push(file)
      }
    }
    return await axios
      .put('/api/v1/file-storage/' + id, {
        ...data,
        deletedFiles,
        newlyAddedFiles,
      })
      .then(({data}) => {
        toast.success('Updated successfully')
        this.setIsUpdated(data, true);
        return data
      })
  }

  delete = async (id: number) => {
    await axios.delete('/api/v1/file-storage/' + id).then(({ data }) => {
      toast.success(
        'Delete is started. You will be notified when the process is completed'
      )
      this.removeFromList(id)
    })
  }

  getById = async (id: number) => {
    return await axios(`/api/v1/file-storage/${id}`).then(({ data }) => {
      this.setFileStorage(data)
      return data
    })
  }

  getFilesById = async (id: number) => {
    return await axios(`/api/v1/file-storage/${id}/files`).then(
      ({ data }) => {
        this.setFileStorage(data)
        return data
      }
    )
  }

  getData = (filters: any = {}) => {
    if (typeof this.cancelTokenSource == 'function') {
      // this.cancelTokenSource()
    }

    if (filters.withIdsFilter && !filters.ids) {
      this.clearList()
      return
    }

    const cancelTokenSource = Axios.CancelToken.source()

    const params: any = {
      ...filters,
      name: filters.searchValue,
      access_filter: filters.sharingStatus,
    }
    delete params.ownership
    delete params.sharingStatus

    this.setLoadingList(true)

    const config: AxiosRequestConfig = {
      cancelToken: cancelTokenSource.token,
      params,
    }
    axios
      .get(`/api/v1/file-storage`, config)
      .then(({ data }) => {
        if (data?.content) {
          runInAction(() => {
            this.list = data.content
            this.totalPages = data.totalPages
            this.totalElements = data.totalElements
          })
        }
      })
      .catch(() => {
        this.clearList()
      })
      .finally(() => {
        this.setLoadingList(false)
      })

    this.cancelTokenSource = cancelTokenSource.cancel
  }

  searchByName = async (name: string) => {
    return await axios
      .get(`/api/v1/file-storage`, {
        params: {
          name,
          size: 10,
        },
      })
      .then(({ data }) => data?.content)
  }

  searchByNameAndDelDeliveryType = async (name: string) => {
    return await axios
      .get(`/api/v1/file-storage`, {
        params: {
          name,
          delDeliveryTypeList: Object.keys(DEL_DELIVERY_TYPE).join(','),
          size: 10,
        },
      })
      .then(({ data }) => data?.content)
  }

  isNameExists = async (name: string) => {
    return await axios(
      `/api/v1/file-storage/name/exists?name=${name}`
    ).then(({ data }) => data)
  }

  getDocumentByIds = async (ids: number[]) => {
    return await axios
      .get('/api/v1/file-storage', {
        params: {
          ids: ids.join(','),
          size: 1000,
        },
      })
      .then(({ data }) => data.content as FileStorage[])
  }

  downloadAllDocumentsInZipFile = async (fileStorageId: number) => {
    return await axios(
      '/api/v1/file-storage/' + fileStorageId + '/files/zip',
      {
        responseType: 'blob',
      }
    ).then(({ data, headers }) => {
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(data);
      link.setAttribute('download', `file-${fileStorageId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    })
  }

  refetchListIds = async (ids: number[]) => {
    const exitsIds = ids.filter(
      (id) => this.list.findIndex((item) => item.id === id) !== -1
    )
    const documents = await this.getDocumentByIds(exitsIds)
    documents.forEach((document: FileStorage) => {
      const index = this.list.findIndex((item) => item.id === document.id)
      runInAction(() => {
        this.list[index] = document
      })
    })
  }

  setFileStorage = (document: FileStorage) => {
    const index = this.list.findIndex(
      (item: FileStorage) => item.id === document.id
    )
    if (index !== -1) {
      this.list[index] = document
    }
    this.entries[`${document.id}`] = document
  }

  setIsUpdated(document: FileStorage, isUpdated: boolean) {
    document.isUpdated = isUpdated;
    this.setFileStorage(document);
  }

  makeFileStoragePrivate = (id: number) => {
    return axios
      .put('/api/v1/file-storage/' + id + '/private/')
      .then(({ data }) => {
        toast.success('The file storage is private now')
        this.setFileStorage(data)
      })
  }

  makeFileStoragePublic = (id: number) => {
    return axios
      .put('/api/v1/file-storage/' + id + '/public/')
      .then(({ data }) => {
        toast.success('The file storage is public now')

        this.setFileStorage(data)
      })
  }

  sendNotificationEmail = (id: number) => {
    return axios.get('/api/v1/file-storage/' + id + '/send-notification-email').then(() => {
      toast.success('The users have been notified.')
    });
  }
    
  exportCSVFileAsDataset = (id:number, fileName: string, newDatasetName: string) => {
    return axios.put('/api/v1/file-storage/' + id + '/export', 
    {
      fileStorageFileName: fileName,
      datasetName: newDatasetName,
    }).then(() => {
      toast.success('The dataset is being created.')
    })
  }
}

export const fileStorages = new FileStoragesStore()
